/* eslint-disable react/require-default-props */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import StatusChangeModal from './StatusChangeModal';

import {
   Section,
   SectionBody,
   SectionHeader,
   SectionTitle,
} from '../../../../styles/layout/Section';

// =============================================================================
// Painel 3 — Transição de status do item.
//
// Regras (espelham backend ScheduleItemService.updateStatus):
//   - REALIZADO exige assigned_technician_employee_id != null OU
//     is_freelance_technician === 1
//   - NAO_REALIZADO exige non_completion_reason_id (modal pede o motivo)
//   - Backend limpa motivo automaticamente quando vai pra PENDENTE/REALIZADO
//
// Auditoria:
//   - updated_at sempre disponível
//   - updatedByUser depende do mini-ajuste no findById; se não vier,
//     mostra apenas a data sem o autor.
// =============================================================================

const STATUS_DEFINITIONS = [
   {
      value: 'PENDENTE',
      label: 'Pendente',
      symbol: '○',
      colors: {
         bg: 'rgba(0,0,0,0.06)',
         border: 'rgba(0,0,0,0.15)',
         text: '#444',
         activeBg: 'rgba(0,0,0,0.1)',
         activeBorder: 'rgba(0,0,0,0.4)',
      },
   },
   {
      value: 'REALIZADO',
      label: 'Realizado',
      symbol: '●',
      colors: {
         bg: 'rgba(46,125,50,0.06)',
         border: 'rgba(46,125,50,0.25)',
         text: '#1b5e20',
         activeBg: 'rgba(46,125,50,0.18)',
         activeBorder: 'rgba(46,125,50,0.65)',
      },
   },
   {
      value: 'NAO_REALIZADO',
      label: 'Não realizado',
      symbol: '✕',
      colors: {
         bg: 'rgba(211,47,47,0.06)',
         border: 'rgba(211,47,47,0.25)',
         text: '#b71c1c',
         activeBg: 'rgba(211,47,47,0.18)',
         activeBorder: 'rgba(211,47,47,0.65)',
      },
   },
];

const ButtonRow = styled.div`
   display: grid;
   grid-template-columns: repeat(3, 1fr);
   gap: 12px;

   @media (max-width: 600px) {
      grid-template-columns: 1fr;
   }
`;

const StatusButton = styled.button`
   padding: 14px 12px;
   border-radius: 10px;
   border: 2px solid
      ${({ $isCurrent, $colors }) =>
         $isCurrent ? $colors.activeBorder : $colors.border};
   background: ${({ $isCurrent, $colors }) =>
      $isCurrent ? $colors.activeBg : $colors.bg};
   color: ${({ $colors }) => $colors.text};
   font-size: 0.85rem;
   font-weight: ${({ $isCurrent }) => ($isCurrent ? 700 : 500)};
   cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
   opacity: ${({ disabled }) => (disabled ? 0.55 : 1)};
   transition: all 120ms ease;

   display: flex;
   align-items: center;
   justify-content: center;
   gap: 8px;

   &:hover:not(:disabled) {
      transform: translateY(-1px);
   }
`;

const Footer = styled.div`
   margin-top: 14px;
   padding-top: 10px;
   border-top: 1px solid ${({ theme }) => theme?.colors?.border ?? 'rgba(0,0,0,0.08)'};
   font-size: 0.7rem;
   color: ${({ theme }) => theme?.colors?.textMuted ?? '#666'};
`;

const ReasonLine = styled.div`
   margin-top: 8px;
   font-size: 0.75rem;
   color: ${({ theme }) => theme?.colors?.textPrimary ?? '#222'};

   strong {
      color: #b71c1c;
      font-weight: 600;
   }
`;

// ─── Helpers ────────────────────────────────────────────────────

function formatDateTimeBr(isoString) {
   if (!isoString) return '—';
   const date = new Date(isoString);
   if (Number.isNaN(date.getTime())) return '—';

   const day = String(date.getDate()).padStart(2, '0');
   const month = String(date.getMonth() + 1).padStart(2, '0');
   const year = date.getFullYear();
   const hours = String(date.getHours()).padStart(2, '0');
   const minutes = String(date.getMinutes()).padStart(2, '0');

   return `${day}/${month}/${year} ${hours}:${minutes}`;
}

// Aplica regra de elegibilidade do backend (ScheduleItemService.updateStatus):
//   - REALIZADO exige técnico atribuído OU is_freelance_technician=1
//   - PENDENTE e NAO_REALIZADO não têm pré-condições além das já tratadas no modal
function getTransitionGuard(item, targetStatus) {
   if (!item) return { disabled: true, tooltip: 'Item não carregado' };

   // Não dá pra "transicionar" pro mesmo status atual
   if (item.item_status === targetStatus) {
      return { disabled: true, tooltip: 'Status atual' };
   }

   if (targetStatus === 'REALIZADO') {
      const hasTechnician = item.assigned_technician_employee_id !== null;
      const isFreelance = item.is_freelance_technician === 1;
      if (!hasTechnician && !isFreelance) {
         return {
            disabled: true,
            tooltip:
               'Atribua um técnico ou marque como freelance antes de marcar como Realizado',
         };
      }
   }

   return { disabled: false, tooltip: '' };
}

// =============================================================================

export default function ItemStatusPanel({ item, onStatusChanged, canChangeStatus }) {
   const [pendingStatus, setPendingStatus] = useState(null);

   if (!item) return null;

   const handleClickStatus = (statusValue) => {
      if (!canChangeStatus) return;
      const guard = getTransitionGuard(item, statusValue);
      if (guard.disabled) return;
      setPendingStatus(statusValue);
   };

   const handleCloseModal = () => setPendingStatus(null);

   const handleStatusChanged = (updatedItem) => {
      setPendingStatus(null);
      onStatusChanged?.(updatedItem);
   };

   // Auditoria — busca por nome em updatedByUser; se não houver, fica só na data.
   const updatedAtLabel = formatDateTimeBr(item.updated_at);
   const updatedByName = item.updatedByUser?.nome ?? null;

   return (
      <>
         <Section>
            <SectionHeader>
               <SectionTitle>Status</SectionTitle>
            </SectionHeader>

            <SectionBody>
               <ButtonRow>
                  {STATUS_DEFINITIONS.map((def) => {
                     const isCurrent = item.item_status === def.value;
                     const guard = getTransitionGuard(item, def.value);
                     const fullyDisabled =
                        !canChangeStatus || guard.disabled || isCurrent;

                     return (
                        <StatusButton
                           key={def.value}
                           type="button"
                           $isCurrent={isCurrent}
                           $colors={def.colors}
                           disabled={fullyDisabled}
                           title={
                              isCurrent
                                 ? 'Status atual'
                                 : !canChangeStatus
                                   ? 'Sem permissão para alterar status'
                                   : guard.tooltip || `Marcar como ${def.label}`
                           }
                           onClick={() => handleClickStatus(def.value)}
                        >
                           <span>{def.symbol}</span>
                           {isCurrent ? `${def.label} (atual)` : def.label}
                        </StatusButton>
                     );
                  })}
               </ButtonRow>

               {/* Mostra motivo quando NAO_REALIZADO (sem o motivo, nem chega aqui no backend) */}
               {item.item_status === 'NAO_REALIZADO' &&
                  item.nonCompletionReason && (
                     <ReasonLine>
                        Motivo: <strong>{item.nonCompletionReason.description}</strong>
                     </ReasonLine>
                  )}

               <Footer>
                  Última atualização: {updatedAtLabel}
                  {updatedByName ? ` · por ${updatedByName}` : ''}
               </Footer>
            </SectionBody>
         </Section>

         <StatusChangeModal
            isOpen={Boolean(pendingStatus)}
            onClose={handleCloseModal}
            item={item}
            targetStatus={pendingStatus}
            onStatusChanged={handleStatusChanged}
         />
      </>
   );
}

ItemStatusPanel.propTypes = {
   item: PropTypes.object,
   onStatusChanged: PropTypes.func,
   canChangeStatus: PropTypes.bool,
};
